import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { civicAPI } from '../domains/elections/api/civic';
import VoteSearchForm from '../domains/elections/components/VoteSearchForm';

function getElectionType(name) {
  const lower = name.toLowerCase();
  if (lower.includes('primary')) return 'Primary';
  if (lower.includes('special')) return 'Special';
  if (lower.includes('runoff')) return 'Runoff';
  return 'General';
}

function getState(ocdDivisionId) {
  const match = ocdDivisionId?.match(/state:([a-z]{2})/);
  return match ? match[1].toUpperCase() : null;
}

const TYPE_STYLES = {
  General: { bg: '#1a1a1a', color: '#fefcf6' },
  Primary: { bg: '#d62828', color: '#fefcf6' },
  Special: { bg: '#fcbf49', color: '#1a1a1a' },
  Runoff: { bg: '#f77f00', color: '#1a1a1a' },
};

function UpcomingElectionsPreview() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['elections'],
    queryFn: () => civicAPI.getElections(),
  });

  const preview = (data?.elections || [])
    .filter((e) => new Date(e.electionDay) >= new Date())
    .sort((a, b) => new Date(a.electionDay) - new Date(b.electionDay))
    .slice(0, 4);

  return (
    <section className="py-16 md:py-24" style={{ background: '#ede5d4' }}>
      <div className="container-custom">
        <div className="flex items-end justify-between mb-10">
          <div>
            <h2
              className="text-3xl md:text-4xl mb-2"
              style={{ fontFamily: "'Permanent Marker', cursive" }}
            >
              WHAT'S COMING UP
            </h2>
            <p
              style={{ fontFamily: "'Patrick Hand', cursive", fontSize: '18px', color: '#6b5f4d' }}
            >
              Don't get caught off guard. Know when to show up.
            </p>
          </div>
          <button
            onClick={() => navigate('/elections')}
            className="hidden sm:flex items-center gap-1.5 font-bold transition-colors shrink-0 no-underline"
            style={{
              fontFamily: "'Patrick Hand', cursive",
              fontSize: '16px',
              color: '#d62828',
              borderBottom: '2px solid #d62828',
            }}
          >
            See all elections &rarr;
          </button>
        </div>

        {isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="p-5 animate-pulse"
                style={{ background: '#fefcf6', border: '2px solid #1a1a1a' }}
              >
                <div className="h-4 bg-neutral-300 w-1/3 mb-3" />
                <div className="h-5 bg-neutral-300 w-3/4 mb-2" />
                <div className="h-4 bg-neutral-300 w-1/2 mt-4" />
              </div>
            ))}
          </div>
        )}

        {!isLoading && preview.length === 0 && (
          <p
            style={{
              fontFamily: "'Patrick Hand', cursive",
              fontSize: '18px',
              color: '#8a7e6b',
              textAlign: 'center',
              padding: '2rem 0',
            }}
          >
            No upcoming elections found. Check back soon.
          </p>
        )}

        {!isLoading && preview.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {preview.map((election, idx) => {
              const type = getElectionType(election.name);
              const state = getState(election.ocdDivisionId);
              const date = new Date(election.electionDay);
              const tilt = idx % 2 === 0 ? 'rotate(-1deg)' : 'rotate(1deg)';
              const typeStyle = TYPE_STYLES[type] || TYPE_STYLES.General;
              return (
                <div
                  key={election.id}
                  className="flex flex-col cursor-pointer transition-transform hover:scale-[1.02] active:opacity-75"
                  style={{
                    background: '#fefcf6',
                    border: '2px solid #1a1a1a',
                    boxShadow: '4px 4px 0px #1a1a1a',
                    transform: tilt,
                    padding: '1.25rem',
                  }}
                  onClick={() => navigate('/elections')}
                >
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span
                      className="inline-block px-2.5 py-0.5 text-xs font-bold uppercase"
                      style={{
                        fontFamily: "'Special Elite', monospace",
                        letterSpacing: '0.08em',
                        background: typeStyle.bg,
                        color: typeStyle.color,
                        border: '1px solid #1a1a1a',
                      }}
                    >
                      {type}
                    </span>
                    {state && (
                      <span
                        className="text-xs font-bold"
                        style={{
                          fontFamily: "'Permanent Marker', cursive",
                          color: '#d62828',
                        }}
                      >
                        {state}
                      </span>
                    )}
                  </div>
                  <p
                    className="flex-1 mb-3"
                    style={{
                      fontFamily: "'Patrick Hand', cursive",
                      fontSize: '15px',
                      color: '#1a1a1a',
                      lineHeight: 1.3,
                    }}
                  >
                    {election.name}
                  </p>
                  <p
                    className="flex items-center gap-1"
                    style={{
                      fontFamily: "'Special Elite', monospace",
                      fontSize: '12px',
                      color: '#6b5f4d',
                    }}
                  >
                    <svg
                      className="w-3.5 h-3.5 shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    {date.toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-6 text-center sm:hidden">
          <button
            onClick={() => navigate('/elections')}
            style={{
              fontFamily: "'Patrick Hand', cursive",
              fontSize: '16px',
              color: '#d62828',
              borderBottom: '2px solid #d62828',
              background: 'none',
              border: 'none',
              borderBlockEnd: '2px solid #d62828',
              cursor: 'pointer',
            }}
          >
            View All Elections &rarr;
          </button>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const navigate = useNavigate();

  const features = [
    {
      marker: '#d62828',
      title: 'FIND WHERE TO VOTE',
      description: 'Your polling place. Maps. Hours. Accessibility. No excuses.',
    },
    {
      marker: '#1a1a1a',
      title: "KNOW WHAT'S ON YOUR BALLOT",
      description: 'Candidates. Measures. Propositions. Research them all before you walk in.',
    },
    {
      marker: '#d62828',
      title: 'GET REMINDED',
      description:
        "Deadlines sneak up. We'll make sure you don't miss registration or election day.",
    },
    {
      marker: '#f77f00',
      title: 'PRINTED VOTER GUIDES',
      description: 'Get a physical guide mailed to your door. Old school works.',
    },
    {
      marker: '#1a1a1a',
      title: 'YOUR DATA STAYS YOURS',
      description: 'Encrypted. Private. No selling your info. Period.',
    },
    {
      marker: '#d62828',
      title: 'REGISTER TO VOTE',
      description: 'Fill it out online. Print it. Mail it. Done. No more putting it off.',
    },
  ];

  return (
    <div>
      {/* ═══ HERO — THE BIG SHOUT ═══ */}
      <section
        className="py-16 md:py-24"
        style={{
          background: '#1a1a1a',
          position: 'relative',
        }}
      >
        {/* Background texture — cross-hatch marks */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            opacity: 0.04,
            backgroundImage: `repeating-linear-gradient(
              45deg,
              transparent,
              transparent 10px,
              #fff 10px,
              #fff 11px
            ), repeating-linear-gradient(
              -45deg,
              transparent,
              transparent 10px,
              #fff 10px,
              #fff 11px
            )`,
          }}
        />

        <div className="container-custom relative">
          <div className="max-w-3xl mx-auto text-center">
            {/* Sticker label */}
            <div className="mb-6 flex justify-center">
              <span
                style={{
                  display: 'inline-block',
                  background: '#fcbf49',
                  color: '#1a1a1a',
                  fontFamily: "'Permanent Marker', cursive",
                  fontSize: '14px',
                  padding: '4px 16px',
                  border: '2px solid #1a1a1a',
                  transform: 'rotate(2deg)',
                  boxShadow: '2px 2px 0px rgba(0,0,0,0.3)',
                }}
              >
                NON-PARTISAN & FREE
              </span>
            </div>

            <h1
              style={{
                fontFamily: "'Permanent Marker', cursive",
                fontSize: 'clamp(2.5rem, 8vw, 5rem)',
                color: '#fefcf6',
                lineHeight: 1,
                marginBottom: '0.5rem',
              }}
            >
              YOUR VOTE IS
            </h1>
            <h1
              style={{
                fontFamily: "'Permanent Marker', cursive",
                fontSize: 'clamp(2.5rem, 8vw, 5rem)',
                color: '#d62828',
                lineHeight: 1,
                marginBottom: '1.5rem',
                transform: 'rotate(-1deg)',
              }}
            >
              YOUR POWER.
            </h1>

            <p
              style={{
                fontFamily: "'Patrick Hand', cursive",
                fontSize: '22px',
                color: '#bfb49e',
                marginBottom: '2rem',
                maxWidth: '500px',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              Enter your address. See your districts, your reps, your ballot. Know exactly what
              you're walking into.
            </p>

            <div className="mb-6">
              <VoteSearchForm placeholder='Try "123 Main St, Austin TX" or "Travis County"' />
            </div>

            <div className="mt-8 mb-2 flex justify-center">
              <div
                style={{
                  background: '#fcbf49',
                  border: '2px solid #1a1a1a',
                  boxShadow: '3px 3px 0px rgba(0,0,0,0.4)',
                  padding: '14px 24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  maxWidth: '480px',
                  width: '100%',
                  transform: 'rotate(-0.5deg)',
                }}
              >
                <span style={{ fontSize: '28px', lineHeight: 1 }}>📬</span>
                <div style={{ textAlign: 'left' }}>
                  <p
                    style={{
                      fontFamily: "'Permanent Marker', cursive",
                      fontSize: '15px',
                      color: '#1a1a1a',
                      marginBottom: '2px',
                    }}
                  >
                    GET YOUR BALLOT GUIDE BY MAIL
                  </p>
                  <p
                    style={{
                      fontFamily: "'Patrick Hand', cursive",
                      fontSize: '14px',
                      color: '#3a2e1e',
                      marginBottom: '6px',
                    }}
                  >
                    We'll print it and send it straight to your door before election day.
                  </p>
                  <button
                    onClick={() => navigate('/mailers/setup')}
                    style={{
                      fontFamily: "'Permanent Marker', cursive",
                      fontSize: '13px',
                      color: '#fefcf6',
                      background: '#d62828',
                      border: '2px solid #1a1a1a',
                      padding: '4px 14px',
                      cursor: 'pointer',
                    }}
                  >
                    Sign up — it's free →
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {/* <button
                onClick={() => navigate('/')}
                style={{
                  fontFamily: "'Patrick Hand', cursive",
                  fontSize: '16px',
                  color: '#fefcf6',
                  background: 'transparent',
                  border: '2px dashed #fefcf6',
                  padding: '8px 20px',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                  e.currentTarget.style.borderStyle = 'solid';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderStyle = 'dashed';
                }}
              >
                Browse all states
              </button> */}
              <button
                onClick={() => navigate('/elections')}
                style={{
                  fontFamily: "'Patrick Hand', cursive",
                  fontSize: '16px',
                  color: '#fefcf6',
                  background: 'transparent',
                  border: '2px dashed #fefcf6',
                  padding: '8px 20px',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                  e.currentTarget.style.borderStyle = 'solid';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderStyle = 'dashed';
                }}
              >
                Upcoming elections
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ FEATURES — CUT & PASTE GRID ═══ */}
      <section className="py-16 md:py-24" style={{ background: '#f5f0e8' }}>
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2
              style={{
                fontFamily: "'Permanent Marker', cursive",
                marginBottom: '0.75rem',
              }}
            >
              WHAT YOU NEED. NOTHING YOU DON'T.
            </h2>
            <p
              style={{
                fontFamily: "'Patrick Hand', cursive",
                fontSize: '20px',
                color: '#6b5f4d',
                maxWidth: '550px',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              Every tool to make your vote count. All in one place.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((feature, index) => {
              const tilts = ['-1.5deg', '1deg', '-0.5deg', '1.5deg', '-1deg', '0.8deg'];
              return (
                <div
                  key={index}
                  className="p-6 transition-transform hover:scale-[1.02]"
                  style={{
                    background: '#fefcf6',
                    border: '2px solid #1a1a1a',
                    boxShadow: '4px 4px 0px #1a1a1a',
                    transform: `rotate(${tilts[index]})`,
                    position: 'relative',
                  }}
                >
                  {/* Tape strip at top */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '-8px',
                      left: '50%',
                      transform: `translateX(-50%) rotate(${index % 2 === 0 ? '2' : '-1'}deg)`,
                      width: '70px',
                      height: '20px',
                      background: 'rgba(252, 191, 73, 0.5)',
                      border: '1px solid rgba(0,0,0,0.06)',
                    }}
                  />

                  {/* Colored marker line */}
                  <div
                    style={{
                      width: '40px',
                      height: '4px',
                      background: feature.marker,
                      marginBottom: '12px',
                    }}
                  />

                  <h3
                    style={{
                      fontFamily: "'Permanent Marker', cursive",
                      fontSize: '18px',
                      color: '#1a1a1a',
                      marginBottom: '8px',
                      lineHeight: 1.2,
                    }}
                  >
                    {feature.title}
                  </h3>
                  <p
                    style={{
                      fontFamily: "'Patrick Hand', cursive",
                      fontSize: '16px',
                      color: '#504636',
                      lineHeight: 1.4,
                    }}
                  >
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ UPCOMING ELECTIONS ═══ */}
      <UpcomingElectionsPreview />

      {/* ═══ CTA — THE CLOSER ═══ */}
      <section className="py-16 md:py-24" style={{ background: '#f5f0e8' }}>
        <div className="container-custom">
          <div
            className="max-w-3xl mx-auto text-center p-8 md:p-12"
            style={{
              background: '#fefcf6',
              border: '3px solid #1a1a1a',
              boxShadow: '6px 6px 0px #1a1a1a',
              transform: 'rotate(-0.5deg)',
              position: 'relative',
            }}
          >
            {/* Corner tape strips */}
            <div
              style={{
                position: 'absolute',
                top: '-6px',
                left: '20px',
                width: '60px',
                height: '18px',
                background: 'rgba(252, 191, 73, 0.5)',
                border: '1px solid rgba(0,0,0,0.06)',
                transform: 'rotate(-5deg)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '-6px',
                right: '20px',
                width: '60px',
                height: '18px',
                background: 'rgba(252, 191, 73, 0.5)',
                border: '1px solid rgba(0,0,0,0.06)',
                transform: 'rotate(3deg)',
              }}
            />

            <h2
              style={{
                fontFamily: "'Permanent Marker', cursive",
                fontSize: 'clamp(1.8rem, 5vw, 2.8rem)',
                marginBottom: '1rem',
              }}
            >
              THEY COUNT ON YOU NOT SHOWING UP.
            </h2>
            <p
              style={{
                fontFamily: "'Patrick Hand', cursive",
                fontSize: '20px',
                color: '#504636',
                marginBottom: '2rem',
                maxWidth: '500px',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              Prove them wrong. Get informed. Get registered. Get loud.
            </p>
            <button
              onClick={() => navigate('/signup')}
              style={{
                fontFamily: "'Permanent Marker', cursive",
                fontSize: '20px',
                color: '#fefcf6',
                background: '#d62828',
                border: '3px solid #1a1a1a',
                boxShadow: '4px 4px 0px #1a1a1a',
                padding: '12px 36px',
                cursor: 'pointer',
                transform: 'rotate(1deg)',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'rotate(1deg) translate(-2px, -2px)';
                e.currentTarget.style.boxShadow = '6px 6px 0px #1a1a1a';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'rotate(1deg)';
                e.currentTarget.style.boxShadow = '4px 4px 0px #1a1a1a';
              }}
            >
              SIGN UP FREE
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
